import React, { useEffect, useState } from "react";
import MobileLayout from "../components/layout/MobileLayout";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast, Toaster } from "sonner";
import { IndianRupee, Crown, Wallet as WalletIcon } from "lucide-react";
import PageHeader from "../components/PageHeader";

export default function WithdrawPage() {
  const { user, refreshUser } = useAuth();
  const [settings, setSettings] = useState({});
  const [stats, setStats] = useState(null);
  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState("upi");
  const [upi, setUpi] = useState("");
  const [bank, setBank] = useState({ holder_name: "", bank_name: "", account_number: "", ifsc: "" });
  const [loading, setLoading] = useState(false);
  const [tick, setTick] = useState(0);
  useEffect(() => {
    api.get("/settings").then(({data}) => setSettings(data));
    api.get("/wallet/stats").then(({ data }) => setStats(data)).catch(() => {});
  }, []);
  // Tick every 1 second so the 24h-cooldown countdown stays live
  useEffect(() => { const id = setInterval(() => setTick(t => t + 1), 1000); return () => clearInterval(id); }, []);

  const minW = settings.min_withdraw ?? 500;
  const wo = settings.withdraw_open_time;
  const wc = settings.withdraw_close_time;
  const num = parseInt(amount) || 0;

  // 24h cooldown: compute remaining time until next_withdraw_at
  const cooldown = (() => {
    if (!stats?.next_withdraw_at) return null;
    const nextMs = new Date(stats.next_withdraw_at).getTime();
    const diff = nextMs - Date.now();
    if (diff <= 0) return null;
    const h = Math.floor(diff / 3600000);
    const m = Math.floor((diff % 3600000) / 60000);
    const s = Math.floor((diff % 60000) / 1000);
    // Also compute the IST clock-time at which they CAN withdraw next
    const istNext = new Date(nextMs);
    const istTime = istNext.toLocaleString("en-IN", { timeZone: "Asia/Kolkata", day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", hour12: true });
    return { h, m, s, istTime };
  })();

  // IST current time check — true if window configured AND we're outside it.
  // Agar admin ne wo ya wc khaali chhod diya → 24×7 open, no check.
  const outsideWindow = (() => {
    if (!wo || !wc) return false;
    try {
      const [oh, om] = wo.split(":").map(Number);
      const [ch, cm] = wc.split(":").map(Number);
      // Date.now() is always UTC ms; add 330 min to get IST equivalent.
      const ist = new Date(Date.now() + 330 * 60 * 1000);
      const cur = ist.getUTCHours() * 60 + ist.getUTCMinutes();
      const open = oh * 60 + om;
      const close = ch * 60 + cm;
      // 00:00 → 23:59 ya 00:00 → 00:00 = always-open shortcut
      if ((open === 0 && close >= 23 * 60 + 59) || open === close) return false;
      if (open <= close) return !(cur >= open && cur <= close);
      // overnight window (e.g. 22:00 → 06:00)
      return !(cur >= open || cur <= close);
    } catch (e) { return false; }
  })();

  const submit = async () => {
    if (cooldown) {
      return toast.error(
        `⚠️ 24 ghante me 1 withdrawal allowed hai. Agli withdrawal ${cooldown.istTime} IST ke baad (baaki ${cooldown.h}h ${cooldown.m}m ${cooldown.s}s).`,
        { duration: 7000 }
      );
    }
    if (outsideWindow) {
      return toast.error(
        `⚠️ अभी निकासी बंद है। कृपया ${wo} से ${wc} के बीच ही withdrawal लगाएँ।`,
        { duration: 6000 }
      );
    }
    if (num < minW) return toast.error(`⚠️ Minimum withdrawal ₹${minW} hai. Aapne ₹${num} request kiya — kam se kam ₹${minW} chahiye.`);
    const body = { amount: num, method };
    if (method === "upi") {
      if (upi.length < 4) return toast.error("Enter valid UPI ID");
      body.upi_id = upi;
    } else {
      if (!bank.holder_name || !bank.bank_name || !bank.account_number || !bank.ifsc) return toast.error("Fill all bank details");
      Object.assign(body, bank);
    }
    setLoading(true);
    try {
      await api.post("/wallet/withdraw", body);
      toast.success("Withdrawal request submitted");
      setAmount(""); setUpi(""); setBank({ holder_name: "", bank_name: "", account_number: "", ifsc: "" });
      refreshUser();
      api.get("/wallet/stats").then(({ data }) => setStats(data)).catch(() => {});
    } catch (e) { toast.error(formatApiError(e)); } finally { setLoading(false); }
  };

  return (
    <MobileLayout>
      <Toaster richColors position="top-center" />
      <PageHeader title="Withdraw Funds" testId="withdraw-header" />
      <div className="px-3 pb-6">

      {/* Compact dep/wd row — minimal, no bulky cards */}
      {stats && (
        <div className="grid grid-cols-2 gap-2 mb-3 text-xs" data-testid="withdraw-stats">
          <div className="rounded-xl bg-emerald-50 border border-emerald-200 px-3 py-2 flex items-center justify-between" data-testid="stat-deposit">
            <span className="text-emerald-700 font-bold tracking-wider">Total Deposit</span>
            <span className="font-black text-emerald-800 tabular-nums">₹{Number(stats.total_deposit || 0).toLocaleString("en-IN")}</span>
          </div>
          <div className="rounded-xl bg-rose-50 border border-rose-200 px-3 py-2 flex items-center justify-between" data-testid="stat-withdraw">
            <span className="text-rose-700 font-bold tracking-wider">Total Withdraw</span>
            <span className="font-black text-rose-800 tabular-nums">₹{Number(stats.total_withdraw || 0).toLocaleString("en-IN")}</span>
          </div>
        </div>
      )}

      {/* 24h cooldown banner (shown only if admin cooldown is ON and user has pending/approved withdraw within 24h) */}
      {stats && cooldown && (
        <div className="mb-3" data-testid="cooldown-row">
          <div data-testid="cooldown-banner" className="rounded-xl px-3 py-2.5" style={{
            background: "linear-gradient(90deg, rgba(244,63,94,0.14), rgba(251,191,36,0.14))",
            border: "1px solid rgba(244,63,94,0.5)",
          }}>
            <div className="flex items-center justify-between gap-2">
              <div>
                <div className="text-[11px] uppercase tracking-widest text-rose-200 font-black">⏰ 24 hour cooldown active</div>
                <div className="text-[10px] text-slate-200 mt-0.5">Agli withdrawal: <span className="font-bold text-amber-200">{cooldown.istTime} IST</span></div>
              </div>
              <div className="font-display font-black tabular-nums text-rose-200 text-base">{cooldown.h}h {String(cooldown.m).padStart(2,"0")}m {String(cooldown.s).padStart(2,"0")}s</div>
            </div>
          </div>
        </div>
      )}

      {stats && stats.pending_withdraw > 0 && (
        <div className="mb-3 bg-amber-50 border border-amber-300 rounded-xl py-1.5 text-center" data-testid="pending-withdraw-banner">
          <span className="text-[11px] text-amber-900 font-bold">
            ⏳ Pending: <span className="tabular-nums">₹{stats.pending_withdraw}</span>
          </span>
        </div>
      )}

      <div
        className="rounded-2xl p-4 shadow-xl"
        style={{
          background: "linear-gradient(180deg, rgba(10,24,51,0.65) 0%, rgba(6,14,40,0.9) 100%)",
          border: "1px solid rgba(34,211,238,0.25)",
          boxShadow: "0 8px 24px rgba(0,0,0,0.4), 0 0 0 1px rgba(34,211,238,0.08) inset",
        }}
        data-testid="withdraw-form"
      >
        {wo && wc && (
          <div
            className="text-[11px] rounded-xl p-2.5 mb-3 text-center font-black tracking-wider"
            style={{
              background: "linear-gradient(90deg, rgba(251,191,36,0.18), rgba(34,211,238,0.14), rgba(251,191,36,0.18))",
              color: "#fde68a",
              border: "1px solid rgba(251,191,36,0.4)",
            }}
          >
            ⏰ WITHDRAWALS OPEN {wo} → {wc} (IST)
          </div>
        )}
        <Label className="text-[11px] uppercase tracking-widest text-cyan-200/80 font-black">Amount (points)</Label>
        <div className="relative mt-1.5">
          <IndianRupee className="w-4 h-4 absolute left-3 top-3.5 text-cyan-300" />
          <Input data-testid="withdraw-amount" inputMode="numeric" value={amount} onChange={(e)=>setAmount(e.target.value.replace(/\D/g,""))}
            className="pl-9 h-12 rounded-xl bg-black/40 border-2 border-cyan-400/30 focus-visible:border-cyan-400 focus-visible:ring-cyan-400 text-lg font-bold text-white placeholder:text-slate-500"
            placeholder={`Min ${minW}`} />
        </div>

        {/* Live red-blinking warnings */}
        {num > 0 && num < minW && (
          <div data-testid="min-withdraw-warning" className="mt-3 blink-red border-2 rounded-xl p-3 font-bold text-center text-sm" style={{ fontFamily: '"Noto Sans Devanagari", system-ui, sans-serif' }}>
            ⚠️ न्यूनतम निकासी ₹{minW} है — आपने ₹{num} माँगा है। कम से कम ₹{minW} दर्ज करें।
          </div>
        )}
        {num > 0 && num > (user?.wallet_balance || 0) && (
          <div data-testid="withdraw-insufficient-warning" className="mt-3 blink-red border-2 rounded-xl p-3 font-bold text-center text-sm" style={{ fontFamily: '"Noto Sans Devanagari", system-ui, sans-serif' }}>
            ⚠️ वॉलेट में सिर्फ ₹{user?.wallet_balance || 0} है — ₹{num} withdraw नहीं कर सकते।
          </div>
        )}

        <div className="mt-3 grid grid-cols-2 gap-2" data-testid="withdraw-method">
          <button onClick={()=>setMethod("upi")} className={`text-sm font-black tracking-wider py-3 rounded-xl border-2 transition ${method==="upi" ? "bg-gradient-to-br from-amber-400 to-yellow-500 text-blue-950 border-yellow-300 shadow-[0_0_14px_rgba(251,191,36,0.4)]" : "bg-black/30 text-cyan-100 border-cyan-400/25"}`} data-testid="method-upi">UPI</button>
          <button onClick={()=>setMethod("bank")} className={`text-sm font-black tracking-wider py-3 rounded-xl border-2 transition ${method==="bank" ? "bg-gradient-to-br from-amber-400 to-yellow-500 text-blue-950 border-yellow-300 shadow-[0_0_14px_rgba(251,191,36,0.4)]" : "bg-black/30 text-cyan-100 border-cyan-400/25"}`} data-testid="method-bank">BANK TRANSFER</button>
        </div>

        {method === "upi" ? (
          <div className="mt-3">
            <Label className="text-[11px] uppercase tracking-widest text-cyan-200/80 font-black">Your UPI ID</Label>
            <Input data-testid="withdraw-upi" value={upi} onChange={(e)=>setUpi(e.target.value)}
              className="mt-1.5 h-12 rounded-xl bg-black/40 border-2 border-cyan-400/30 focus-visible:border-cyan-400 text-white placeholder:text-slate-500 font-bold"
              placeholder="yourname@bank" autoComplete="off" />
          </div>
        ) : (
          <div className="mt-3 space-y-2.5">
            <div><Label className="text-[11px] uppercase tracking-widest text-cyan-200/80 font-black">Account Holder Name</Label><Input data-testid="wd-holder" value={bank.holder_name} onChange={(e)=>setBank({...bank, holder_name: e.target.value})} className="mt-1.5 h-12 rounded-xl bg-black/40 border-2 border-cyan-400/30 focus-visible:border-cyan-400 text-white" autoComplete="off" /></div>
            <div><Label className="text-[11px] uppercase tracking-widest text-cyan-200/80 font-black">Bank Name</Label><Input data-testid="wd-bank-name" value={bank.bank_name} onChange={(e)=>setBank({...bank, bank_name: e.target.value})} className="mt-1.5 h-12 rounded-xl bg-black/40 border-2 border-cyan-400/30 focus-visible:border-cyan-400 text-white" autoComplete="off" /></div>
            <div><Label className="text-[11px] uppercase tracking-widest text-cyan-200/80 font-black">Account Number</Label><Input data-testid="wd-account" inputMode="numeric" value={bank.account_number} onChange={(e)=>setBank({...bank, account_number: e.target.value.replace(/\D/g,"")})} className="mt-1.5 h-12 rounded-xl bg-black/40 border-2 border-cyan-400/30 focus-visible:border-cyan-400 text-white tabular-nums" autoComplete="off" /></div>
            <div><Label className="text-[11px] uppercase tracking-widest text-cyan-200/80 font-black">IFSC Code</Label><Input data-testid="wd-ifsc" value={bank.ifsc} onChange={(e)=>setBank({...bank, ifsc: e.target.value.toUpperCase()})} className="mt-1.5 h-12 rounded-xl bg-black/40 border-2 border-cyan-400/30 focus-visible:border-cyan-400 text-white uppercase tracking-wider font-black" autoComplete="off" /></div>
          </div>
        )}

        <Button data-testid="withdraw-submit" onClick={submit} disabled={loading || !!cooldown} className="w-full mt-4 h-12 rounded-xl bg-gradient-to-r from-cyan-500 to-blue-600 text-white font-black tracking-wider hover:from-cyan-400 shadow-xl disabled:opacity-50">{loading ? "SUBMITTING…" : cooldown ? `WAIT ${cooldown.h}h ${String(cooldown.m).padStart(2,"0")}m` : "REQUEST WITHDRAWAL"}</Button>
      </div>
      </div>
    </MobileLayout>
  );
}

function StatCard({ label, value, sub, color, testId }) {
  return (
    <div
      data-testid={testId}
      className={`rounded-2xl bg-gradient-to-br ${color} text-white p-2.5 shadow-lg border border-white/20 relative overflow-hidden`}
    >
      <div className="absolute -top-3 -right-3 w-12 h-12 bg-white/10 rounded-full blur-xl" />
      <div className="relative">
        <div className="text-[9px] uppercase tracking-widest font-black opacity-80">{label}</div>
        <div className="font-display font-black text-lg leading-tight tabular-nums mt-0.5 truncate">₹{Number(value || 0).toLocaleString("en-IN")}</div>
        <div className="text-[10px] font-bold tracking-wider opacity-90 mt-0.5">{sub}</div>
      </div>
    </div>
  );
}
